import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Check, X, AlertCircle } from 'lucide-react';
import './CancelBooking.css';

const CancelBooking = () => {
  const { token } = useParams();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [cancelled, setCancelled] = useState(false);
  const [error, setError] = useState(null);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchBookingDetails();
  }, [token]);

  const fetchBookingDetails = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/email/booking-details/${token}`);
      const data = await response.json().catch(() => ({}));

      if (response.ok && data.success) {
        setBooking(data.booking);
        if (data.booking.status === 'cancelled') {
          setCancelled(true);
        }
      } else {
        // Surface the backend's helpful detail (e.g. "This link has
        // expired.") instead of a generic "Booking not found" — the
        // most common misread of this page by real users.
        setError(data.detail || (response.status === 404
          ? 'This link has expired or is no longer valid.'
          : 'We couldn\u2019t load your booking. Please try again, or contact the shop.'));
      }
    } catch (err) {
      console.error('Error fetching booking:', err);
      setError('Could not reach the server. Check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    setCancelling(true);
    setError(null);
    try {
      const response = await fetch(`${BACKEND_URL}/api/email/cancel-booking`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token })
      });

      const data = await response.json().catch(() => ({}));

      if (response.ok && (data.success || data.already_cancelled)) {
        setCancelled(true);
      } else {
        // CRITICAL: the backend's `detail` carries the actionable
        // reason (409 min-notice window with the shop phone, 404
        // expired link, etc). Hiding it behind "Failed to cancel"
        // is what makes real users think the link is broken.
        setError(data.detail
          || (response.status === 409 ? 'This booking can no longer be cancelled online — please contact the shop directly.'
            : response.status === 404 ? 'This cancellation link has expired.'
            : 'Something went wrong cancelling your booking. Please contact the shop.'));
      }
    } catch (err) {
      console.error('Error cancelling booking:', err);
      setError('Could not reach the server. Please try again or contact the shop directly.');
    } finally {
      setCancelling(false);
    }
  };

  if (loading) {
    return (
      <div className="cancel-page">
        <div className="cancel-container">
          <div className="loading-state">Loading...</div>
        </div>
      </div>
    );
  }

  if (error && !booking) {
    return (
      <div className="cancel-page">
        <div className="cancel-container">
          <div className="error-state" data-testid="cancel-error-state">
            <AlertCircle size={48} color="#ef4444" />
            <h2>Can't open this link</h2>
            <p>{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (cancelled) {
    return (
      <div className="cancel-page">
        <div className="cancel-container">
          <div className="success-state">
            <div className="success-icon">
              <Check size={32} color="white" />
            </div>
            <h2>Booking Cancelled</h2>
            <p>Your appointment has been cancelled successfully.</p>
            <div className="booking-summary">
              <p><strong>{booking.service_name}</strong></p>
              <p>with {booking.barber_name}</p>
              <p>at {booking.shop_name}</p>
            </div>
            <p className="footer-text">The time slot is now available for other customers.</p>
          </div>
        </div>
      </div>
    );
  }

  const startDate = new Date(booking.starts_at);
  const formattedDate = startDate.toLocaleDateString('en-GB', { 
    weekday: 'long', 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });
  const formattedTime = startDate.toLocaleTimeString('en-GB', { 
    hour: '2-digit', 
    minute: '2-digit' 
  });

  return (
    <div className="cancel-page">
      <div className="cancel-container">
        <div className="cancel-header">
          <h1>Cancel Booking</h1>
          <p>Are you sure you want to cancel this appointment?</p>
        </div>

        <div className="booking-details">
          <div className="detail-row">
            <span className="label">Shop</span>
            <span className="value">{booking.shop_name}</span>
          </div>
          <div className="detail-row">
            <span className="label">Customer</span>
            <span className="value">{booking.customer_name}</span>
          </div>
          <div className="detail-row">
            <span className="label">Service</span>
            <span className="value">{booking.service_name}</span>
          </div>
          <div className="detail-row">
            <span className="label">Barber</span>
            <span className="value">{booking.barber_name}</span>
          </div>
          <div className="detail-row">
            <span className="label">Date</span>
            <span className="value">{formattedDate}</span>
          </div>
          <div className="detail-row">
            <span className="label">Time</span>
            <span className="value">{formattedTime}</span>
          </div>
          <div className="detail-row">
            <span className="label">Duration</span>
            <span className="value">{booking.duration_min} minutes</span>
          </div>
        </div>

        <div className="cancel-actions">
          {error && (
            <div
              className="cancel-inline-error"
              role="alert"
              data-testid="cancel-inline-error"
              style={{
                background: '#fef2f2', border: '1px solid #fecaca',
                color: '#991b1b', padding: '12px 14px', borderRadius: 10,
                fontSize: 14, lineHeight: 1.4, marginBottom: 12,
              }}
            >{error}</div>
          )}
          <button
            className="btn-cancel"
            onClick={handleCancel}
            disabled={cancelling}
          >
            {cancelling ? 'Cancelling...' : 'Yes, Cancel Booking'}
          </button>
          <p className="cancel-note">This action cannot be undone. You'll need to book again if you change your mind.</p>
        </div>
      </div>
    </div>
  );
};

export default CancelBooking;